import { useState, useEffect, useRef } from "react";
import { useUser, useLeaves, useCreateLeave, useUpdateLeave, useGetPermissions, useTotalStaff, useDeleteLeave } from "../Use-auth";
import Button from "../ui/Button";
import Input from "../ui/Input";
import Select from "../ui/Select";
import SelectedDate from "../ui/SelectedDate";
import { CalendarRange, X, Pencil, Eye, Star, Trash2 } from "lucide-react";
import moment from "moment";
import Pagination from "./Pagination";
import DateRangePicker from "./DateRangePicker";
import { format, differenceInSeconds } from "date-fns";
import SkeletonPage from "./SkeletonPage ";
import PermissionPopup from "./PermissionPopup";

const InfoRow = ({ label, value, className }) => (
    <tr className="text-[10px] sm:text-[12px]">
        <td className="px-4 py-2 font-medium text-gray-600 w-1/3">
            {label}
        </td>

        <td className={`px-4 py-2 text-gray-800 ${className} inline-block `}>
            {value || "-"}
        </td>
    </tr >
);

export default function LeaveTracking() {
    const { data: user } = useUser();
    const { data: existingPermissions, refetch } = useGetPermissions();
    const isHRorAdmin = user?.department === "HR" || user?.department === "Admin" || user?.department === "Management";
    const canEditLeave =
        user?.department === "HR" ||
        user?.department === "Admin" ||
        (user?.department === "Management" && user?.userId === "HUB103");

    const isAdmin = user?.department === "Admin";
    const [showForm, setShowForm] = useState(false);
    const [subject, setSubject] = useState("");
    const [type, setType] = useState("Personal");
    const [dateRange, setDateRange] = useState({ startDate: null, endDate: null });
    const [message, setMessage] = useState("");
    const [manualDate, setManualDate] = useState(false);
    const [successMsg, setSuccessMsg] = useState("");
    const [errorMsg, setErrorMsg] = useState("");
    const [hrReply, setHrReply] = useState("");
    const [viewingLeave, setViewingLeave] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const formsPerPage = 20;
    const [editingLeaveId, setEditingLeaveId] = useState(null);
    const { data: leavesData = [], isLoading } = useLeaves(user?.userId, isHRorAdmin);
    const createLeave = useCreateLeave();
    const updateLeave = useUpdateLeave();
    const calendarRef = useRef(null);
    const actionRef = useRef(null);
    const initializedEmails = useRef(false);

    const [showDatePicker, setShowDatePicker] = useState(false);
    const [emails, setEmails] = useState("");
    const today = new Date();
    const { data: employee } = useTotalStaff();
    const [halfType, setHalfType] = useState("");
    const [fromHour, setFromHour] = useState("09");
    const [fromMinute, setFromMinute] = useState("00");
    const [fromPeriod, setFromPeriod] = useState("AM");
    const [toHour, setToHour] = useState("10");
    const [toMinute, setToMinute] = useState("00");
    const [toPeriod, setToPeriod] = useState("AM");
    const deleteLeave = useDeleteLeave();

    const [filterDateRange, setFilterDateRange] = useState({
        startDate: today,
        endDate: today,
    });

    const [loadingAction, setLoadingAction] = useState({
        leaveId: null,
        type: null,
    });

    const currentUserPermissions = isAdmin
        ? {
            management: {
                leave_view: true,
            },
            hr: {
                leave_view: true,
            },
            employees: {
                leave_view: true,
            }
        }
        : existingPermissions?.find(p => p.userId === user?.userId);

    const canViewHome =
        isAdmin || currentUserPermissions?.management?.leave_view === true
        || currentUserPermissions?.hr?.leave_view === true
        || currentUserPermissions?.employees?.leave_view === true;

    const getDefaultPersonalDate = () => {
        const today = new Date();
        today.setDate(today.getDate() + 3);
        return today;
    };

    useEffect(() => {
        if (type === "Personal" && !manualDate) {
            const defaultDate = getDefaultPersonalDate();

            setDateRange({
                startDate: defaultDate,
                endDate: defaultDate,
            });
        }

        if (type === "Urgent" && !manualDate) {
            const today = new Date();

            setDateRange({
                startDate: today,
                endDate: today,
            });
        }
    }, [type, manualDate]);

    const generateMessage = (start, end) => {
        const startStr = start ? moment(start).format("YYYY-MM-DD") : "";
        const endStr = end ? moment(end).format("YYYY-MM-DD") : "";

        const dateText =
            start && end && end.getTime() !== start.getTime()
                ? `${startStr} to ${endStr}`
                : startStr;

        return dateText;
    };

    const handleApplyDateRange = ({ startDate, endDate }) => {
        if (type === "Personal") {
            const minDate = getDefaultPersonalDate();

            if (moment(startDate).isBefore(minDate, "day")) {
                alert(
                    `For Personal leave, date must be ${moment(minDate).format("YYYY-MM-DD")} or later`
                );

                setDateRange({
                    startDate: minDate,
                    endDate: minDate,
                });

                setManualDate(false);

                return;
            }
        }

        setDateRange({
            startDate,
            endDate,
        });

        setManualDate(true);
    };

    const convertTo24Hour = (hour, minute, period) => {
        let h = parseInt(hour);

        if (period === "PM" && h !== 12) h += 12;
        if (period === "AM" && h === 12) h = 0;

        return `${String(h).padStart(2, "0")}:${minute}`;
    };

    useEffect(() => {
        if (type === "Haff Day") {
            if (halfType === "First Half") {
                setFromHour("09");
                setFromMinute("00");
                setFromPeriod("AM");

                setToHour("01");
                setToMinute("00");
                setToPeriod("PM");
            }

            if (halfType === "Second Half") {
                setFromHour("01");
                setFromMinute("00");
                setFromPeriod("PM");

                setToHour("06");
                setToMinute("00");
                setToPeriod("PM");
            }
        }
    }, [halfType, type]);

    useEffect(() => {
        if (subject && type && dateRange.startDate && dateRange.endDate && message) {
            setErrorMsg("");
        }
    }, [subject, type, dateRange, message]);

    const handleAddLeave = () => {

        if (!subject || !type || !dateRange.startDate || !dateRange.endDate || !message) {
            setErrorMsg("All fields are required");
            return;
        }

        let fromTime24 = "";
        let toTime24 = "";

        if (type === "Short Leave") {
            fromTime24 = convertTo24Hour(fromHour, fromMinute, fromPeriod);
            toTime24 = convertTo24Hour(toHour, toMinute, toPeriod);

            if (fromTime24 >= toTime24) {
                setErrorMsg("To Time must be greater than From Time");
                return;
            }
            const from = moment(fromTime24, "HH:mm");
            const to = moment(toTime24, "HH:mm");

            const diffMinutes = to.diff(from, "minutes");

            if (diffMinutes !== 120) {
                setErrorMsg("Short Leave must be exactly 2 hours");
                return;
            }
        }

        if (type === "Haff Day" && !halfType) {
            setErrorMsg("Please select First Half or Second Half");
            return;
        }

        createLeave.mutateAsync({
            userId: user?.userId,
            userName: user?.fullName,
            userEmail: user?.email,
            department: user?.department,
            subject,
            type,
            startDate: moment(dateRange.startDate).format("YYYY-MM-DD"),
            endDate: moment(dateRange.endDate).format("YYYY-MM-DD"),
            message,
            fromTime: fromTime24,
            toTime: toTime24,
            halfType: type === "Haff Day" ? halfType : "",
            sendToEmails: emails
                .split(",")
                .map(e => e.trim())
                .filter(e => e),
        })
            .then(() => {
                setSuccessMsg("Leave submitted successfully!");
                setTimeout(() => {
                    setSuccessMsg("");
                }, 1000);
                setSubject("");
                setFromHour("09");
                setFromMinute("00");
                setFromPeriod("AM");
                setToHour("10");
                setToMinute("00");
                setToPeriod("AM");
                setType("Personal");
                setManualDate(false);
                setShowForm(false);
                setErrorMsg("")
            })
            .catch((err) => setErrorMsg(err.message));
    };

    const handleUpdateLeave = (userId, leaveId, status) => {
        setLoadingAction({
            leaveId,
            type: status,
        });

        updateLeave.mutate(
            { userId, leaveId, status, hrReply },
            {
                onSuccess: () => {
                    setHrReply("");
                    setEditingLeaveId(null);
                    setLoadingAction({ leaveId: null, type: null });
                },
                onError: (err) => {
                    alert(err.message);
                    setLoadingAction({ leaveId: null, type: null });
                },
            }
        );
    };

    const allLeaves = (leavesData || [])
        .flatMap(userLeave =>
            (userLeave.leaves || []).map(leave => ({
                ...leave,
                userName: userLeave.userName,
                userId: userLeave.userId,
                department: userLeave.department,
            }))
                .filter(leave => {
                    if (user?.department === "Management") {
                        return leave.department !== "HR";
                    }
                    return true;
                })
        )
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

    const totalPages = Math.ceil(allLeaves.length / formsPerPage);

    const filteredLeaves = allLeaves.filter(leave => {
        if (!filterDateRange.startDate || !filterDateRange.endDate) return true;

        // filterDateRange comes from picker as local Date — format as plain date string
        const start = moment(filterDateRange.startDate).format("YYYY-MM-DD");
        const end = moment(filterDateRange.endDate).format("YYYY-MM-DD");

        // leave dates from DB are UTC ISO — use utc() to read them correctly
        const leaveStart = moment.utc(leave.startDate).format("YYYY-MM-DD");
        const leaveEnd = leave.endDate
            ? moment.utc(leave.endDate).format("YYYY-MM-DD")
            : leaveStart;
        const appliedDate = moment.utc(leave.createdAt).format("YYYY-MM-DD");

        const isInLeaveRange = leaveStart <= end && leaveEnd >= start;
        const isInAppliedRange = appliedDate >= start && appliedDate <= end;

        return isInLeaveRange || isInAppliedRange;
    });

    const currentForms = filteredLeaves.slice(
        (currentPage - 1) * formsPerPage,
        currentPage * formsPerPage
    );

    useEffect(() => {
        setCurrentPage(1);
    }, [filterDateRange]);

    const handlePageChange = (page) => setCurrentPage(page);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (actionRef.current && !actionRef.current.contains(event.target)) {
                setEditingLeaveId(null);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const initializedRef = useRef(false);

    useEffect(() => {
        if (!leavesData.length) return;

        if (initializedRef.current) return;
        initializedRef.current = true;

        const today = new Date();

        setFilterDateRange({
            startDate: today,
            endDate: today,
        });
    }, [leavesData]);

    const getDefaultEmails = () => {
        if (!employee || !user) return "";

        const employeeList = Array.isArray(employee)
            ? employee
            : employee?.data || [];

        const activeEmployees = employeeList.filter(
            emp => emp?.employeeStatus === "Active"
        );

        let emailsList = [];

        if (user.department !== "HR" && user.department !== "Management") {
            activeEmployees.forEach(emp => {
                if (
                    emp?.departmentName === "HR" ||
                    emp?.departmentName === "Management"
                ) {
                    if (emp?.officeEmail) {
                        emailsList.push(emp.officeEmail);
                    }
                }
            });
        } else {
            activeEmployees.forEach(emp => {
                if (emp?.departmentName === "Admin") {
                    if (emp?.officeEmail) {
                        emailsList.push(emp.officeEmail);
                    }
                }
            });
        }

        return emailsList.join(", ");
    };

    useEffect(() => {
        if (showForm && employee && user && !initializedEmails.current) {
            const defaultEmails = getDefaultEmails();
            setEmails(defaultEmails);
            initializedEmails.current = true;
        }
    }, [showForm, employee, user]);

    const TOTAL_ANNUAL_LEAVES = 12;

    const getUsedLeaves = () => {
        if (!leavesData || !user) return 0;

        let count = 0;

        leavesData.forEach(userLeave => {
            if (userLeave.userId === user.userId) {
                (userLeave.leaves || []).forEach(leave => {
                    if (leave.status !== "Approved") return;

                    const type = leave.type?.toLowerCase().trim();
                    if (type === "short leave") return;

                    const start = moment(leave.startDate).startOf("day");
                    const end = leave.endDate
                        ? moment(leave.endDate).startOf("day")
                        : start;

                    let days = end.diff(start, "days") + 1;

                    if (type === "half day" || type === "haff day") {
                        days = 0.5;
                    }

                    count += days;
                });
            }
        });

        return count;
    };

    const usedLeaves = getUsedLeaves();
    const remainingLeaves = TOTAL_ANNUAL_LEAVES - usedLeaves;

    const calculateLeaveDays = (leave) => {
        const type = leave.type?.toLowerCase().trim();
        if (type === "short leave") return 0;
        if (type === "half day" || type === "haff day") return 0.5;

        const start = moment(leave.startDate).startOf("day");
        const end = leave.endDate
            ? moment(leave.endDate).startOf("day")
            : start;

        return end.diff(start, "days") + 1;
    };

    if (!canViewHome) {
        return (
            <div>
                <div className="relative h-[80vh] overflow-hidden z-1">
                    <PermissionPopup />
                </div>
            </div>
        );
    }

    const safeFormat = (date) => {
        if (!date) return "";
        return moment(date).format("DD-MM-YYYY"); // use moment, not date-fns
    };

    // add this function in LeaveTracking component
    const handleDeleteLeave = (leaveId) => {
        if (!window.confirm("Are you sure you want to delete this leave?")) return;
        deleteLeave.mutate(leaveId);
    };

    return (
        <div>
            <div>
                <div className="flex flex-col sm:flex-row justify-end sm:items-center gap-3 w-full">
                    {/* Date Picker */}
                    <div className="w-full sm:w-auto">
                        <div
                            className="cursor-pointer border text-[11px] sm:text-sm px-3 py-2 rounded-md bg-white flex items-center gap-2 w-full justify-start"
                            onClick={() => setShowDatePicker(true)}
                        >
                            <CalendarRange size={18} />
                            <span>{safeFormat(filterDateRange.startDate) || "Start"}</span>
                            <span>→</span>
                            <span>{safeFormat(filterDateRange.endDate) || "End"}</span>
                        </div>
                        <DateRangePicker
                            open={showDatePicker}
                            selectedRange={filterDateRange}
                            onApply={({ startDate, endDate }) =>
                                setFilterDateRange({ startDate, endDate })
                            }
                            onClose={() => setShowDatePicker(false)}
                        />
                    </div>

                    {!isAdmin && (
                        <div className="w-full sm:w-auto">
                            <Button
                                className="w-full sm:w-auto text-[11px] sm:text-sm gap-2.5 text-white bg-gray-900 justify-center"
                                onClick={() => { setShowForm(true); setErrorMsg('') }}
                            >
                                Add Leave
                            </Button>
                        </div>
                    )}
                </div>

                {showForm && (
                    <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50 transition-colors duration-300" onClick={() => setShowForm(false)}>
                        <div className="bg-white  rounded-lg shadow-xl p-2 sm:p-6 relative w-full max-w-7xl h-4/5  animate-scaleIn overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                            <button
                                className="absolute top-1 right-1 sm:top-4 sm:right-4 text-gray-500 hover:text-gray-900"
                                onClick={() => setShowForm(false)}
                            >
                                <X size={20} />
                            </button>

                            <h2 className=" font-bold mb-6 text-gray-800 text-[14px] sm:text-[16px]">Add Leave</h2>
                            <div>
                                <div className="flex flex-col flex-1 mt-[20px]">
                                    <Input
                                        label="Send To"
                                        type="text"
                                        value={emails}
                                        onChange={(e) => setEmails(e.target.value)}
                                        placeholder="example@gmail.com, test@gmail.com"
                                        className={`${errorMsg && !emails ? "border-red-500" : "border-gray-300"
                                            } rounded px-2 py-2 focus:outline-none focus:ring-2 focus:ring-gray-300`}
                                        required
                                    />
                                </div>
                                <div className="flex flex-col flex-1 mt-[20px]">
                                    <Input
                                        label="Subject"
                                        value={subject}
                                        onChange={(e) => setSubject(e.target.value)}
                                        placeholder="Enter subject"
                                        className={`${errorMsg && !subject ? "border-red-500" : "border-gray-300"
                                            } rounded px-2 py-2 focus:outline-none focus:ring-2 focus:ring-gray-300`}
                                        required
                                    />
                                </div>

                                <div className="flex flex-col flex-1 mt-[20px]">
                                    <Select
                                        label="Type"
                                        name="type"
                                        value={type}
                                        onChange={(e) => {
                                            setType(e.target.value);
                                            setManualDate(false);
                                        }}
                                        options={["Short Leave", "Half Day", "Personal", "Urgent", "Casual Leave",
                                            "Work From Home"
                                        ]}
                                        className={`${errorMsg && !type ? "border-red-500" : "border-gray-300"
                                            } rounded px-2 py-2 `}
                                        required
                                    />
                                </div>
                                {type === "Short Leave" && (
                                    <div className="flex gap-4 mt-[20px]">

                                        <div className="flex flex-col flex-1">
                                            <label className="text-sm font-medium mb-1 text-gray-700 text-[12px] sm:text-sm">
                                                From Time
                                            </label>

                                            <div className="flex gap-2">
                                                <select
                                                    value={fromHour}
                                                    onChange={(e) => setFromHour(e.target.value)}
                                                    className="border rounded px-2 py-2"
                                                >
                                                    {[...Array(12)].map((_, i) => {
                                                        const h = String(i + 1).padStart(2, "0");
                                                        return <option key={h}>{h}</option>;
                                                    })}
                                                </select>

                                                <select
                                                    value={fromMinute}
                                                    onChange={(e) => setFromMinute(e.target.value)}
                                                    className="border rounded px-2 py-2 text-[12px] sm:text-sm"
                                                >
                                                    {["00", "15", "30", "45"].map(m => (
                                                        <option key={m}>{m}</option>
                                                    ))}
                                                </select>

                                                <select
                                                    value={fromPeriod}
                                                    onChange={(e) => setFromPeriod(e.target.value)}
                                                    className="border rounded px-2 py-2 text-[12px] sm:text-sm"
                                                >
                                                    <option>AM</option>
                                                    <option>PM</option>
                                                </select>

                                            </div>
                                        </div>

                                        <div className="flex flex-col flex-1">
                                            <label className="text-sm font-medium mb-1 text-gray-700 text-[12px] sm:text-sm">
                                                To Time
                                            </label>

                                            <div className="flex gap-2">

                                                <select
                                                    value={toHour}
                                                    onChange={(e) => setToHour(e.target.value)}
                                                    className="border rounded px-2 py-2 text-[12px] sm:text-sm"
                                                >
                                                    {[...Array(12)].map((_, i) => {
                                                        const h = String(i + 1).padStart(2, "0");
                                                        return <option key={h}>{h}</option>;
                                                    })}
                                                </select>

                                                <select
                                                    value={toMinute}
                                                    onChange={(e) => setToMinute(e.target.value)}
                                                    className="border rounded px-2 py-2 text-[12px] sm:text-sm"
                                                >
                                                    {["00", "15", "30", "45"].map(m => (
                                                        <option key={m}>{m}</option>
                                                    ))}
                                                </select>

                                                <select
                                                    value={toPeriod}
                                                    onChange={(e) => setToPeriod(e.target.value)}
                                                    className="border rounded px-2 py-2 text-[12px] sm:text-sm"
                                                >
                                                    <option>AM</option>
                                                    <option>PM</option>
                                                </select>

                                            </div>
                                        </div>

                                    </div>
                                )}

                                {type === "Half Day" && (
                                    <div className="mt-4">
                                        <label className="block text-[12px] sm:text-sm font-medium mb-1 text-gray-700">
                                            Half Day Type
                                        </label>

                                        <select
                                            value={halfType}
                                            onChange={(e) => setHalfType(e.target.value)}
                                            className="w-full border rounded px-3 py-2 text-[12px] sm:text-sm"
                                        >
                                            <option value="">Select Half</option>
                                            <option value="First Half">First Half (Morning)</option>
                                            <option value="Second Half">Second Half (Afternoon)</option>
                                        </select>
                                    </div>
                                )}

                                <div className="flex flex-col flex-1 relative mt-[20px] text-[12px] sm:text-sm">
                                    <label className="text-sm font-medium mb-1 text-gray-700">Date
                                        <span className="text-red-500 ml-1">*</span>
                                    </label>
                                    <div
                                        ref={calendarRef}
                                        className="border border-gray-300 rounded px-2 py-2 flex items-center justify-between cursor-pointer hover:border-gray-400"
                                    >
                                        <span className="text-gray-800">
                                            {dateRange.startDate
                                                ? `${moment(dateRange.startDate).format("YYYY-MM-DD")}${dateRange.endDate &&
                                                    dateRange.endDate.getTime() !== dateRange.startDate.getTime()
                                                    ? " - " + moment(dateRange.endDate).format("YYYY-MM-DD")
                                                    : ""
                                                }`
                                                : "Select date"}
                                        </span>
                                        <CalendarRange className="ml-2 text-gray-600" />
                                    </div>

                                    <SelectedDate
                                        anchorRef={calendarRef}
                                        selectedRange={dateRange}
                                        onApply={handleApplyDateRange}
                                        onClose={() => { }}
                                    />
                                </div>

                                <div className="flex flex-col flex-1 mt-[20px] text-[12px] sm:text-sm">
                                    <label className="text-sm font-medium mb-1 text-gray-700">Message
                                        <span className="text-red-500 ml-1">*</span>
                                    </label>
                                    <textarea
                                        className={`border ${errorMsg && !message ? "border-red-500" : "border-gray-300"
                                            } rounded px-2 py-2 w-full text-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-300 h-[120px]`}
                                        value={message}
                                        onChange={(e) => setMessage(e.target.value)}
                                        placeholder="Enter message"
                                    />
                                </div>
                            </div>

                            <div className="flex justify-end gap-3 mt-[20px]">
                                <Button
                                    variant="success"
                                    onClick={handleAddLeave}
                                    disabled={createLeave.isPending}
                                >
                                    {createLeave.isPending ? (
                                        <>
                                            Submitting...
                                            <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                                        </>
                                    ) : (
                                        "Submit"
                                    )}
                                </Button>
                            </div>
                            {successMsg && <p className="text-green-600 mt-3 text-right text-[12px]">{successMsg}</p>}
                            {errorMsg && <p className="text-red-600 mt-3 text-right text-[12px]">{errorMsg}</p>}

                        </div>
                    </div>
                )}

                <div className="mt-3 sm:mt-6">
                    <h2 className="text-[14px] sm:text-[16px] md:text-[20px] sm:mb-4 font-semibold text-gray-800 border-b border-gray-300 pb-2">
                        Leave Requests
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6 mb-4">
                        {user?.department !== "Admin" && (
                            <Card
                                title="Leaves Remaining"
                                value={remainingLeaves}
                                icon={<Star className="w-12 h-12 opacity-80" />}
                                bg={
                                    remainingLeaves < 0
                                        ? "from-red-500 to-red-600"
                                        : "from-purple-500 to-purple-600"
                                }
                            />
                        )}

                    </div>

                    {isLoading ? (
                        <div className="space-y-3">
                            {[...Array(6)].map((_, i) => (
                                <SkeletonPage key={i} />
                            ))}
                        </div>
                    ) : leavesData.length === 0 ? (
                        <p>No leaves found.</p>
                    ) : (
                        <div className="overflow-x-auto bg-white rounded-lg shadow">
                            <table className="min-w-full border border-gray-200 text-[10px] sm:text-[12px]">
                                <thead className="bg-gray-100">
                                    <tr>
                                        <th className="border px-3 py-2 text-left">User Name</th>
                                        <th className="border px-3 py-2 text-left hidden xs:table-cell">Subject</th>
                                        <th className="border px-3 py-2 text-left hidden xs:table-cell">Type</th>
                                        <th className="border px-3 py-2 text-left hidden xs:table-cell">Applied Date</th>
                                        <th className="border px-3 py-2 text-left  hidden custom:table-cell">Leave Date</th>
                                        <th className="border px-3 py-2 text-left hidden zx:table-cell">No. of Days</th>
                                        {/* <th className="border px-3 py-2 text-left hidden custom:table-cell">Message</th> */}
                                        <th className="border px-3 py-2 text-left">Status</th>
                                        <th className="border px-3 py-2 text-left hidden custom:table-cell">HR Reply</th>
                                        <th className="border px-3 py-2 text-center">Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {currentForms.map((leave) => (
                                        <tr key={leave._id} className="hover:bg-gray-50">
                                            <td className="border px-3 py-2 font-medium text-gray-800">
                                                {leave.userName}
                                            </td>
                                            <td className="border px-3 py-2 hidden xs:table-cell max-w-[200px] truncate">{leave.subject}</td>
                                            <td className="border px-3 py-2 hidden xs:table-cell">{leave.type}</td>
                                            <td className="border px-3 py-2 hidden xs:table-cell">
                                                {moment(leave.createdAt).format("M/D/YYYY")}
                                            </td>
                                            <td className="border px-3 py-2 whitespace-nowrap hidden custom:table-cell ">
                                                {moment(leave.startDate).format("M/D/YYYY")}
                                                {leave.endDate && leave.endDate !== leave.startDate
                                                    ? " - " + moment(leave.endDate).format("M/D/YYYY")
                                                    : ""}
                                            </td>
                                            <td className="border px-3 py-2 text-center font-medium hidden zx:table-cell">
                                                {calculateLeaveDays(leave)}
                                            </td>
                                            {/* <td className="border px-3 py-2 max-w-[220px] truncate hidden custom:table-cell">
                                                {leave.message}
                                            </td> */}

                                            <td className="border px-3 py-2 font-semibold">
                                                <span
                                                    className={
                                                        leave.status === "Approved"
                                                            ? "text-green-600"
                                                            : leave.status === "Rejected"
                                                                ? "text-red-600"
                                                                : "text-orange-600"
                                                    }
                                                >
                                                    {leave.status}
                                                </span>
                                            </td>

                                            <td className="border px-3 py-2 max-w-[220px] truncate hidden custom:table-cell">
                                                {leave.hrReply || "-"}
                                            </td>

                                            <td className="border px-3 py-2 ">
                                                <div className="flex gap-2 justify-left">
                                                    <div className="cursor-pointer">
                                                        <Eye
                                                            size={18}
                                                            className=" p-[2px] rounded cursor-pointer hover:bg-orange-100"
                                                            onClick={() => setViewingLeave(leave)}
                                                            color="orange"
                                                        />
                                                    </div>

                                                    {canEditLeave &&
                                                        leave.status === "Pending" &&
                                                        leave.userId !== user?.userId && (<div>
                                                            {editingLeaveId !== leave._id && (
                                                                <button
                                                                    onClick={() => setEditingLeaveId(leave._id)}
                                                                    className="text-gray-600 hover:text-gray-900 flex justify-center w-[100%]"
                                                                    title="Edit"
                                                                >
                                                                    <Pencil size={18} color="blue" className="p-[2px] rounded cursor-pointer hover:bg-blue-100" />
                                                                </button>
                                                            )}
                                                            {editingLeaveId === leave._id && (
                                                                <div ref={actionRef} className="flex flex-col gap-1 mt-1">
                                                                    <textarea
                                                                        className="border rounded px-2 py-1 text-[12px] sm:text-sm"
                                                                        placeholder="HR reply..."
                                                                        value={hrReply}
                                                                        onChange={(e) => setHrReply(e.target.value)}
                                                                    />

                                                                    <div className="flex gap-1">
                                                                        <Button
                                                                            variant="success"
                                                                            onClick={() => handleUpdateLeave(leave.userId, leave._id, "Approved")}
                                                                            disabled={
                                                                                loadingAction.leaveId === leave._id &&
                                                                                loadingAction.type === "Approved"
                                                                            }
                                                                        >
                                                                            {loadingAction.leaveId === leave._id &&
                                                                                loadingAction.type === "Approved" && (
                                                                                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-1"></span>
                                                                                )}

                                                                            Approve
                                                                        </Button>
                                                                        <Button
                                                                            variant="danger"
                                                                            onClick={() => handleUpdateLeave(leave.userId, leave._id, "Rejected")}
                                                                            disabled={
                                                                                loadingAction.leaveId === leave._id &&
                                                                                loadingAction.type === "Rejected"
                                                                            }
                                                                        >
                                                                            {loadingAction.leaveId === leave._id &&
                                                                                loadingAction.type === "Rejected" && (
                                                                                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-1"></span>
                                                                                )}

                                                                            Reject
                                                                        </Button>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>)}

                                                    {canEditLeave && editingLeaveId !== leave._id && (
                                                        <div
                                                            className="p-1 rounded cursor-pointer hover:bg-red-100"
                                                            onClick={() => handleDeleteLeave(leave._id)}
                                                        >
                                                            <Trash2 size={14} color="red" className="cursor-pointer" />
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
                {viewingLeave && (
                    <div
                        className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-3 sm:px-4"
                        onClick={() => setViewingLeave(null)}
                    >
                        <div
                            className="bg-white relative animate-scaleIn rounded-lg w-full max-w-[700px] shadow-lg h-[85vh] overflow-hidden"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="sticky top-0 z-10 flex justify-between items-center bg-gray-900 p-2 sm:px-4 py-1 sm:py-3">
                                <h2 className="text-white text-[12px] sm:text-[16px]">
                                    Leave Details
                                </h2>

                                <button
                                    className="text-white hover:text-red-400"
                                    onClick={() => setViewingLeave(null)}
                                >
                                    <X size={20} />
                                </button>
                            </div>

                            <div className="px-2 sm:p-6 overflow-y-auto h-full space-y-4">
                                <table className="w-full border border-gray-200 rounded-md mb-6">
                                    <tbody className="divide-y">
                                        <InfoRow label="User Name" value={viewingLeave.userName} />
                                        <InfoRow label="Subject" value={viewingLeave.subject} />
                                        <InfoRow label="Type" value={viewingLeave.type} />

                                        {viewingLeave.halfType && (
                                            <InfoRow label="Half Type" value={viewingLeave.halfType} />
                                        )}

                                        {(viewingLeave.fromTime || viewingLeave.toTime) && (
                                            <InfoRow
                                                label="Time Slot"
                                                value={
                                                    viewingLeave.fromTime && viewingLeave.toTime
                                                        ? `${viewingLeave.fromTime} - ${viewingLeave.toTime}`
                                                        : viewingLeave.fromTime || viewingLeave.toTime
                                                }
                                            />
                                        )}

                                        <InfoRow
                                            label="Leave Date"
                                            value={
                                                moment(viewingLeave.startDate).format("M/D/YYYY") +
                                                (viewingLeave.endDate &&
                                                    viewingLeave.endDate !== viewingLeave.startDate
                                                    ? " - " + moment(viewingLeave.endDate).format("M/D/YYYY")
                                                    : "")
                                            }
                                        />

                                        <InfoRow
                                            label="Message"
                                            value={
                                                viewingLeave.message
                                                    ? viewingLeave.message.split("\n").map((line, i) => (
                                                        <div key={i}>{line}</div>
                                                    ))
                                                    : "-"
                                            }
                                        />
                                        <InfoRow
                                            label="HR Reply"
                                            value={
                                                viewingLeave.hrReply
                                                    ? viewingLeave.hrReply.split("\n").map((line, i) => (
                                                        <div key={i}>{line}</div>
                                                    ))
                                                    : "-"
                                            }
                                        />
                                        <tr className="text-[10px] sm:text-[12px]">
                                            <td className="px-4 py-2 font-medium text-gray-600 w-1/3">
                                                Status
                                            </td>
                                            <td
                                                className={`px-4 py-2 font-medium inline-block ${viewingLeave.status === "Approved"
                                                    ? "text-green-600"
                                                    : viewingLeave.status === "Rejected"
                                                        ? "text-red-600"
                                                        : "text-orange-600"
                                                    }`}
                                            >
                                                {viewingLeave.status || "-"}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}

                {/* Pagination */}
                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    handlePageChange={handlePageChange}
                />
            </div>
        </div>
    );
}

const Card = ({ title, value, icon, bg }) => (
    <div
        className={`bg-gradient-to-r ${bg} text-white rounded-xl sm:rounded-2xl p-3 sm:p-6 shadow-lg flex justify-between items-center w-full group`}
    >
        <div className="flex items-center justify-between w-full">
            <div>
                <p className="text-[10px] sm:text-sm xl:text-base font-medium opacity-80">
                    {title}
                </p>
                <p className="text-[14px] sm:text-lg xl:text-3xl font-bold">
                    {value}
                </p>
            </div>

            <div
                className="w-4 h-4 sm:w-6 sm:h-6 md:w-8 md:h-8 xl:w-12 xl:h-12 opacity-80
                flex items-center justify-center
                [&_svg]:w-full [&_svg]:h-full
                group-hover:scale-125 transition-transform duration-300"
            >
                {icon}
            </div>
        </div>
    </div>
);